import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// services
import projectService from "services/projects.js";
import logger from "utils/logger";

export default function CardProjectTable({ color, onEditProject, onDeleteProject, onNewProject }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  // Load projects from API
  const loadProjects = async (currentFilters = {}) => {
    try {
      setLoading(true);
      // Always include disabled projects in the project management screen
      // so users can see and re-enable them
      const response = await projectService.getProjects({ ...currentFilters, include_disabled: 1 });
      setProjects(response.projects || response.data || []);
      setError(null);
    } catch (err) {
      logger.error('Error loading projects:', err);
      setError('Error al cargar los proyectos');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadProjects();
  }, []);

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    
    // Apply local filtering for search
    if (filterType === 'search') {
      // This would typically be done server-side, but for now we filter locally
      loadProjects();
    }
  };

  // Handle project deletion
  const handleDeleteProject = async (projectId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este proyecto? Esto también eliminará todas las tareas asociadas.')) {
      try {
        await projectService.deleteProject(projectId);
        await loadProjects(); // Reload projects
        if (onDeleteProject) onDeleteProject(projectId);
      } catch (err) {
        logger.error('Error deleting project:', err);
        alert('Error al eliminar el proyecto');
      }
    }
  };

  // Filter projects based on search
  const filteredProjects = projects.filter(project => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      project.project_name?.toLowerCase().includes(search) ||
      project.project_code?.toLowerCase().includes(search)
    );
  });

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Gestión de Proyectos
              </h3>
            </div>
            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
              {onNewProject && (
                <button
                  className="bg-indigo-500 text-white active:bg-indigo-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={onNewProject}
                >
                  Nuevo Proyecto
                </button>
              )}
            </div>
          </div>
          
          {/* Search Filter */}
          <div className="flex flex-wrap items-center mt-4 space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar proyecto..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className={
                  "text-sm px-5 py-2 placeholder-blueGray-300 relative bg-white rounded border border-blueGray-300 outline-none focus:outline-none focus:ring w-full " +
                  (color === "light" ? "text-blueGray-600" : "text-white bg-lightBlue-800 border-lightBlue-700")
                }
              />
            </div>
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          {/* Projects table */}
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Código
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Nombre
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Estado
                </th>
                <th
                  className={
                    "px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                    (color === "light"
                      ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                      : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                  }
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="4" className="px-3 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    </div>
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan="4" className="px-3 py-4 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && filteredProjects.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-3 py-4 text-center text-gray-500">
                    {projects.length === 0 ? "No hay proyectos para mostrar" : "No se encontraron proyectos con el criterio de búsqueda"}
                  </td>
                </tr>
              )}
              
              {!loading && !error && filteredProjects.map((project) => (
                <tr
                  key={project.id}
                  style={{
                    backgroundColor: !project.enabled ? '#fef2f2' : 'transparent',
                    opacity: !project.enabled ? 0.7 : 1
                  }}
                >
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <div>
                      <span
                        className={
                          "font-semibold " +
                          (color === "light" ? "text-blueGray-600" : "text-white")
                        }
                      >
                        {project.project_code}
                      </span>
                    </div>
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs p-4">
                    <span
                      className={color === "light" ? "text-blueGray-600" : "text-white"}
                      title={project.project_name}
                    >
                      {project.project_name.length > 30 ? `${project.project_name.substring(0, 30)}...` : project.project_name}
                    </span>
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    {project.enabled !== false ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <span className="mr-1">✓</span> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        <span className="mr-1">✗</span> Inactivo
                      </span>
                    )}
                  </td>
                  <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <div className="flex space-x-1">
                      <button
                        className="text-indigo-600 hover:text-indigo-900 text-xs font-semibold px-2"
                        onClick={() => onEditProject && onEditProject(project)}
                        title="Editar proyecto"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900 text-xs font-semibold px-1"
                        onClick={() => handleDeleteProject(project.id)}
                        title="Eliminar proyecto"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CardProjectTable.defaultProps = {
  color: "light",
};

CardProjectTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  onEditProject: PropTypes.func,
  onDeleteProject: PropTypes.func,
  onNewProject: PropTypes.func,
};